import React, { useState } from 'react';
import { UserCheck, MapPin, Baby, Check, Plus, Minus, AlertCircle, Gift, Droplets } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const iconMap = {
  UserCheck,
  MapPin,
  Baby,
  Droplets,
};

const ExtraCard = ({ 
  extra, 
  isSelected, 
  onToggle, 
  inputValue, 
  onInputChange,
  quantity,
  onQuantityChange,
  error,
}) => {
  const Icon = iconMap[extra.icon] || MapPin;
  const price = extra.price || extra.defaultPrice;
  const isQuantityType = extra.inputType === 'quantity';
  const isFreeGift = extra.isFreeGift || false;
  const originalPrice = extra.originalPrice || null;
  const isAutoIncluded = extra.isAutoIncluded || false;

  return (
    <div
      className={`bg-white rounded-xl overflow-hidden transition-all duration-200 ${
        isSelected
          ? 'ring-2 ring-[#FF8C00] shadow-md'
          : isFreeGift 
            ? 'ring-2 ring-green-500 shadow-md' 
            : 'border border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
      data-testid={`extra-card-${extra.id.toLowerCase()}`}
    >
      {/* FREE GIFT Banner */}
      {isFreeGift && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 flex items-center justify-center gap-2">
          <Gift className="h-4 w-4" />
          <span className="text-sm font-bold uppercase tracking-wider">Free Gift - Included</span>
        </div>
      )}

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isFreeGift 
                ? 'bg-green-500' 
                : isSelected 
                  ? 'bg-[#FF8C00]' 
                  : 'bg-gray-100'
            }`}>
              <Icon className={`h-5 w-5 ${isFreeGift || isSelected ? 'text-white' : 'text-gray-600'}`} />
            </div>
            <div>
              <h3 className="font-semibold text-black">{extra.name}</h3>
              <p className="text-sm text-gray-500">{extra.description}</p>
            </div>
          </div>
          
          {/* Price Badge */}
          <div className="text-right">
            {isFreeGift ? (
              <div className="flex flex-col items-end">
                {originalPrice && (
                  <span className="text-sm text-gray-400 line-through">
                    €{originalPrice}
                  </span>
                )}
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-600">
                  FREE
                </span>
              </div>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold bg-[#FF8C00]/10 text-[#FF8C00]">
                +{extra.currency}{price}
                {isQuantityType && quantity > 1 && ` × ${quantity}`}
              </span>
            )}
          </div>
        </div>

        {/* Auto-included notice for free gifts */}
        {isFreeGift && isAutoIncluded && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-green-700 flex items-center gap-2">
              <Check className="h-4 w-4" />
              Automatically included with your booking
            </p>
          </div>
        )}

        {/* Input Section (shown when selected or auto-included) */}
        {(isSelected || isAutoIncluded) && (extra.inputType === 'text' || isQuantityType) && (
          <div className={`mt-4 pt-4 ${!isFreeGift ? 'border-t border-gray-100' : ''}`}>
            {extra.inputType === 'text' && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">
                  {extra.inputLabel}
                  {extra.inputRequired && <span className="text-[#FF8C00] ml-1">*</span>}
                </label>
                <Input
                  type="text"
                  placeholder={extra.inputPlaceholder}
                  value={inputValue || ''}
                  onChange={(e) => onInputChange(extra.id, e.target.value)}
                  className={`h-10 text-black placeholder:text-gray-400 ${error ? 'border-[#FF8C00] focus:ring-[#FF8C00]' : ''}`}
                  data-testid={`extra-input-${extra.id.toLowerCase()}`}
                />
                {extra.inputHelp && !error && (
                  <p className="text-xs text-gray-400">{extra.inputHelp}</p>
                )}
                {error && (
                  <div className="flex items-center gap-1.5 text-[#FF8C00]">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span className="text-xs">{error}</span>
                  </div>
                )}
              </div>
            )}

            {isQuantityType && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-600">
                  {extra.inputLabel}
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => onQuantityChange(extra.id, Math.max(1, (quantity || 1) - 1))}
                    className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                    disabled={quantity <= 1}
                    data-testid={`extra-qty-decrease-${extra.id.toLowerCase()}`}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center font-semibold text-black">
                    {quantity || 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => onQuantityChange(extra.id, Math.min(extra.maxQuantity, (quantity || 1) + 1))}
                    className="w-10 h-10 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                    disabled={quantity >= extra.maxQuantity}
                    data-testid={`extra-qty-increase-${extra.id.toLowerCase()}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-400">
                    (max {extra.maxQuantity})
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Toggle Button - Different for free gifts */}
        {!isAutoIncluded && (
          <div className="mt-4">
            <Button
              type="button"
              onClick={() => onToggle(extra)}
              className={`w-full transition-all duration-200 ${
                isSelected
                  ? 'bg-black text-white hover:bg-black/90'
                  : 'bg-[#FF8C00] text-white hover:bg-[#E67E00]'
              }`}
              data-testid={`extra-toggle-${extra.id.toLowerCase()}`}
            >
              {isSelected ? (
                <span className="flex items-center gap-2">
                  <Check className="h-4 w-4" />
                  Added
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add
                </span>
              )}
            </Button>
          </div>
        )}

        {/* Already included button for free gifts */}
        {isAutoIncluded && (
          <div className="mt-4">
            <div className="w-full py-3 px-4 bg-green-100 text-green-700 rounded-lg font-semibold text-center flex items-center justify-center gap-2">
              <Check className="h-5 w-5" />
              Included in Your Booking
            </div>
          </div>
        )}
      </div>

      {/* Active Lane Indicator */}
      {(isSelected || isAutoIncluded) && (
        <div className={`h-1 ${isFreeGift ? 'bg-green-500' : 'bg-[#FF8C00]'}`} />
      )}
    </div>
  );
};

export default ExtraCard;
